import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { TrashIcon, Cog6ToothIcon, EllipsisVerticalIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../shared/UIComponents';
import disciplineConfig from '../../../services/disciplineConfigService';
import EditableField from '../../shared/EditableField';
import MultiSelectDropdown from '../../shared/MultiSelectDropdown';
import OffenceCard from '../OffenceCard';
import { useSchool } from '@/contexts/SchoolContext';

const SEVERITY_LEVELS = [
  { id: 1, name: 'Routine' },
  { id: 2, name: 'Moderate' },
  { id: 3, name: 'Serious' },
  { id: 4, name: 'Critical' }
];

const ZeroToleranceRuleForm = ({ rule = null, ruleId = null, categoryKey = null, onSave, onDelete, availableOffences = [], isRecognitionMode = false }) => {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  const prevRuleId = useRef(rule?.id);

  // Initialize with proper defaults
  const [data, setData] = useState(() => {
    const defaults = {
      type: 'zero-tolerance',  // CRITICAL: This identifies the rule type!
      title: isRecognitionMode ? 'Spotlight Rule' : 'Zero Tolerance Rule',
      consequenceType: 'detention',
      offenseTypes: [], // Array of offense IDs
      notifyParents: true,
      notifyPolice: false,
      notifyAdmin: false,
      groups: [],
      active: true
    };

    if (!rule) return defaults;

    return {
      type: 'zero-tolerance',  // ALWAYS zero-tolerance for this form
      title: rule.title || defaults.title,
      consequenceType: rule.consequenceType || defaults.consequenceType,
      consequenceTypeId: rule.consequenceTypeId || null,
      offenseTypes: rule.offenseTypes || defaults.offenseTypes,
      notifyParents: rule.notifyParents !== undefined ? rule.notifyParents : defaults.notifyParents,
      notifyPolice: rule.notifyPolice !== undefined ? rule.notifyPolice : defaults.notifyPolice,
      notifyAdmin: rule.notifyAdmin !== undefined ? rule.notifyAdmin : defaults.notifyAdmin,
      groups: rule.groups || defaults.groups,
      active: rule.active !== undefined ? rule.active : defaults.active,
      ...(rule.id && { id: rule.id })
    };
  });

  const [showConfig, setShowConfig] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showOffenseSelector, setShowOffenseSelector] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [consequenceOptions, setConsequenceOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);

  const menuRef = useRef();
  const offenseSelectorRef = useRef();

  // Load consequence types from Firestore
  useEffect(() => {
    const loadConsequenceTypes = async () => {
      try {
        const types = await disciplineConfig.getConsequenceTypes(schoolId);
        setConsequenceOptions(types.map(c => ({
          value: c.value,
          label: c.label,
          singular: c.singular,
          plural: c.plural,
          id: c.id  // Include the Firestore document ID
        })));
      } catch (error) {
        console.error('Error loading consequence types:', error);
      }
    };
    loadConsequenceTypes();
  }, [schoolId]);

  // Load grades from Firestore
  useEffect(() => {
    const loadGrades = async () => {
      try {
        const options = await disciplineConfig.getGradeSelectOptions(schoolId);
        setGradeOptions(options);
      } catch (error) {
        console.error('Error loading grades:', error);
      }
    };
    loadGrades();
  }, [schoolId]);

  // Update data when we get a different rule
  useEffect(() => {
    if (rule?.id && rule.id !== prevRuleId.current) {
      prevRuleId.current = rule.id;

      const newData = {
        type: 'zero-tolerance',  // ALWAYS zero-tolerance for this form
        title: rule.title || (isRecognitionMode ? 'Spotlight Rule' : 'Zero Tolerance Rule'),
        consequenceType: rule.consequenceType || 'detention',
        consequenceTypeId: rule.consequenceTypeId || null,
        offenseTypes: rule.offenseTypes || [],
        notifyParents: rule.notifyParents !== undefined ? rule.notifyParents : true,
        notifyPolice: rule.notifyPolice !== undefined ? rule.notifyPolice : false,
        notifyAdmin: rule.notifyAdmin !== undefined ? rule.notifyAdmin : false,
        groups: rule.groups || [],
        active: rule.active !== undefined ? rule.active : true,
        ...(rule.id && { id: rule.id })
      };
      setData(newData);
    }
  }, [rule]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (offenseSelectorRef.current && !offenseSelectorRef.current.contains(event.target)) {
        setShowOffenseSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { save: debouncedSave } = useAutoSave(onSave, 100);

  const update = useCallback((updates) => {
    // If updating consequenceType, also update the corresponding ID field
    if (updates.consequenceType) {
      const typeOption = consequenceOptions.find(opt => opt.value === updates.consequenceType);
      if (typeOption) {
        updates.consequenceTypeId = typeOption.id;
      }
    }

    let newData;
    setData(current => {
      newData = { ...current, ...updates };
      return newData;
    });

    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'zero-tolerance', actualRuleId);
  }, [debouncedSave, categoryKey, ruleId, rule?.id, consequenceOptions]);

  // Sortable setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule?.id || 'temp' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };


  // Get offense objects from IDs (handles both real IDs and all-level-X keys)
  const getSelectedOffenses = () => {
    return data.offenseTypes.map(id => {
      // Handle "All Level X" entries
      const levelMatch = id.match(/^all-level-(\d+)$/);
      if (levelMatch) {
        const levelId = parseInt(levelMatch[1]);
        const level = SEVERITY_LEVELS.find(l => l.id === levelId);
        return { id, name: isRecognitionMode ? `All Level ${levelId} achievements` : `All Level ${levelId} offences`, isLevelCard: true, levelName: level?.name };
      }
      const offense = availableOffences.find(o => o.id === id);
      return offense || { id, name: id }; // Fallback if offense not found
    }).filter(Boolean);
  };

  // Add offense to the list
  const addOffense = (offense) => {
    if (!data.offenseTypes.includes(offense.id)) {
      update({ offenseTypes: [...data.offenseTypes, offense.id] });
    }
    setShowOffenseSelector(false);
  };

  // Add "All Level X" card
  const addAllAtLevel = (levelId) => {
    const levelKey = `all-level-${levelId}`;
    if (!data.offenseTypes.includes(levelKey)) {
      update({ offenseTypes: [...data.offenseTypes, levelKey] });
    }
    setShowOffenseSelector(false);
  };

  // Remove offense from the list
  const removeOffense = (offenseId) => {
    update({ offenseTypes: data.offenseTypes.filter(id => id !== offenseId) });
  };

  // Filter out already selected offenses
  const availableOffensesFiltered = availableOffences.filter(
    offense => !data.offenseTypes.includes(offense.id)
  );

  // Helper: check if an offence belongs to a given level
  const offenceHasLevel = (offence, levelId) => {
    if (offence.levels && offence.levels.includes(levelId)) return true;
    if (offence.level === levelId) return true;
    return false;
  };

  // Build available "All Level X" options (only for levels not already selected)
  const availableLevelCards = SEVERITY_LEVELS.filter(level => {
    const levelKey = `all-level-${level.id}`;
    if (data.offenseTypes.includes(levelKey)) return false;
    // Only show if there are offences at this level
    return availableOffences.some(o => offenceHasLevel(o, level.id));
  });

  // Use centralized configuration
  // consequenceOptions and gradeOptions are now loaded via useEffect above

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag Handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg
            className="w-4 h-4 text-base-content/50"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Editable Title */}
          <div className="mb-3">
            <input
              key={rule?.id || 'new'}
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || (isRecognitionMode ? 'Spotlight Rule' : 'Zero Tolerance Rule');
                if (newTitle !== data.title) {
                  update({ title: newTitle });
                }
                setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className={`text-lg font-medium bg-transparent border-0 p-0 w-full outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
          </div>

          {/* Header row with sentence and controls */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-base">
              {isRecognitionMode ? 'Instant' : 'Immediate'}{' '}
              <EditableField
                field="consequenceType"
                value={data.consequenceType}
                options={consequenceOptions}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              {' '}for:
            </div>

            <div className="flex items-center gap-2">
              {/* Active toggle */}
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                className={`toggle toggle-sm ${data.active ? 'toggle-success' : ''}`}
              />

              {/* Three dots menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 hover:bg-base-200 rounded"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setShowConfig(!showConfig);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-base-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {showConfig ? 'Less options' : 'More options'}
                      </button>
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel'
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) {
                                await onDelete(deleteId);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Offense selection area - Full width container with fixed button position */}
          <div className="pt-4 px-4 pb-2 bg-base-50 border border-base-200 rounded-lg min-h-[100px] flex flex-col">
            {/* Offense cards area */}
            <div className="flex flex-wrap gap-2 flex-1 mb-3">
              {getSelectedOffenses().map((offense) => (
                <div key={offense.id} className="relative group">
                  {offense.isLevelCard ? (
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-base-300 bg-base-200/50 text-sm font-medium text-base-content/70 italic">
                      {offense.name}
                    </div>
                  ) : (
                    <OffenceCard
                      offence={offense}
                      category={offense.category}
                      variant="button"
                      showLevel={false}
                      className="relative"
                    />
                  )}
                  <button
                    onClick={() => removeOffense(offense.id)}
                    className="absolute -top-1 -right-1 bg-base-200 text-base-content rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-base-300 shadow-sm"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* Fixed add button at bottom center */}
            <div className="flex justify-center">
              <div className="relative" ref={offenseSelectorRef}>
                <button
                  onClick={() => setShowOffenseSelector(!showOffenseSelector)}
                  className="px-3 py-2 text-base-content/60 rounded-lg text-sm hover:bg-base-100 hover:text-base-content transition-colors flex items-center gap-1.5"
                >
                  <PlusIcon className="w-4 h-4" />
                  {isRecognitionMode ? 'Add achievement' : 'Add offense'}
                </button>

                {/* Offense selector dropdown */}
                <AnimatePresence>
                  {showOffenseSelector && (availableOffensesFiltered.length > 0 || availableLevelCards.length > 0) && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute left-1/2 transform -translate-x-1/2 mt-1 w-80 max-h-64 overflow-y-auto bg-white border border-base-200 rounded-lg shadow-lg z-10"
                    >
                      <div className="p-2 space-y-1">
                        {availableLevelCards.length > 0 && (
                          <>
                            {availableLevelCards.map(level => {
                              const count = availableOffences.filter(o => offenceHasLevel(o, level.id)).length;
                              return (
                                <button
                                  key={`all-level-${level.id}`}
                                  onClick={() => addAllAtLevel(level.id)}
                                  className="flex items-center gap-3 w-full p-3 rounded-lg border border-dashed border-base-300 bg-base-200/50 hover:bg-base-200 transition-colors cursor-pointer"
                                >
                                  <span className="text-sm font-medium text-base-content/70 italic">
                                    All Level {level.id} &mdash; {level.name}
                                  </span>
                                  <span className="badge badge-xs badge-ghost">{count}</span>
                                </button>
                              );
                            })}
                            <div className="border-t border-base-200 my-1" />
                          </>
                        )}
                        {availableOffensesFiltered.map(offense => (
                          <OffenceCard
                            key={offense.id}
                            offence={offense}
                            category={offense.category}
                            variant="list"
                            showLevel={true}
                            showDescription={true}
                            clickable={true}
                            onClick={() => addOffense(offense)}
                            className="hover:bg-base-50"
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Notification badges */}
          {(data.notifyPolice || data.notifyAdmin) && (
            <div className="flex gap-2 mt-3">
              {data.notifyPolice && (
                <span className="badge badge-error badge-sm">Notify police</span>
              )}
              {data.notifyAdmin && (
                <span className="badge badge-warning badge-sm">Notify admin</span>
              )}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showConfig && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            style={{ overflow: 'hidden' }}
          >
            <div className="mt-4 pt-4 border-t border-base-200 space-y-4">
              {/* Grade targeting */}
              <MultiSelectDropdown
                label="TARGET GROUPS"
                value={data.groups}
                options={gradeOptions}
                onChange={(newGroups) => update({ groups: newGroups })}
                placeholder="All grades"
              />

              {/* Notification options */}
              <div>
                <label className="text-sm font-medium text-base-content/70 mb-2 block">NOTIFICATIONS</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.notifyParents}
                      onChange={(e) => update({ notifyParents: e.target.checked })}
                      className="checkbox checkbox-sm"
                    />
                    <span className="text-sm">Notify parents immediately</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.notifyPolice}
                      onChange={(e) => update({ notifyPolice: e.target.checked })}
                      className="checkbox checkbox-sm"
                    />
                    <span className="text-sm">Notify police</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.notifyAdmin}
                      onChange={(e) => update({ notifyAdmin: e.target.checked })}
                      className="checkbox checkbox-sm"
                    />
                    <span className="text-sm">Notify administration</span>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default ZeroToleranceRuleForm;